// pages/UserProfile.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Avatar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Divider,
  Paper,
} from "@mui/material";
import Select from "react-select";
import axiosInstance from "../utils/axiosInstance";
import ResetPassword from "./UserProfile/ResetPassword";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export default function VerticalTabs() {
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Refs for state search
  const stateFetchIdRef = useRef(0);
  const stateSearchTimeoutRef = useRef(null);

  // Refs for city search
  const cityFetchIdRef = useRef(0);
  const citySearchTimeoutRef = useRef(null);
  const selectedCountryIdRef = useRef(null);
  const selectedStateRef = useRef(null);

  const [UserProfile, setUserProfile] = useState("");
  const [UserfName, setUserfName] = useState("");
  const [UserlName, setUserlName] = useState("");
  const [UserEmail, setUserEmail] = useState("");
  const [UserAddress, setUserAddress] = useState("");
  const [UserCountry, setUserCountry] = useState("");
  const [UserCity, setUserCity] = useState("");
  const [UserState, setUserState] = useState("");
  const [UserZip, setUserZip] = useState("");
  const [UserTimezone, setUserTimezone] = useState("UTC");
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");

  useEffect(() => {
    document.title = "User Profile | KlonIT Web Application";

    const fetchSelections = async () => {
      try {
        const [countriesRes, timezonesRes] = await Promise.all([
          axiosInstance.get(`/api/selections/countries`),
          axiosInstance.get(`/api/selections/timezones`),
        ]);
        setCountryOptions(countriesRes.data.data);
        setTimezoneOptions(timezonesRes.data.data);
      } catch (error) {
        console.error("Failed to fetch selection data:", error);
        setMessage("Error: Could not load country and timezone lists.");
      }
    };

    const fetchProfileData = () => {
      axiosInstance
        .get(`/get-user-profile`)
        .then((response) => {
          const responseData = response.data.data;
          if (responseData && responseData.length > 0) {
            let profile_data = responseData[0];
            setUserProfile(profile_data.profile_image);
            setUserfName(profile_data.fname);
            setUserlName(profile_data.lname);
            setUserEmail(profile_data.email);
            setUserAddress(profile_data.address_one);
            setUserCountry(profile_data.country);
            setUserCity(profile_data.city);
            setUserState(profile_data.state);
            setUserZip(profile_data.zip);
            setUserTimezone(profile_data.timezone || "UTC");
          } else {
            const user = JSON.parse(localStorage.getItem("user"));
            if (user && user.user_email) {
              setUserEmail(user.user_email);
            }
          }
        })
        .catch((error) => {
          setMessage("Error fetching for User Profile:", error);
        });
    };

    fetchSelections();
    fetchProfileData();
  }, []);

  const handleChange = (event, newValue) => setValue(newValue);

  // Fetch states by country ID with optional search term
  const fetchStatesByCountry = useCallback(async (countryId, searchTerm = '') => {
    if (!countryId) {
      setStateOptions([]);
      return;
    }

    const fetchId = ++stateFetchIdRef.current;
    setLoadingStates(true);

    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await axiosInstance.get(`/api/selections/countries/${countryId}/states`, { params });

      if (fetchId === stateFetchIdRef.current && response.data.success) {
        setStateOptions(response.data.data || []);
      }
    } catch (error) {
      if (fetchId === stateFetchIdRef.current) {
        console.error("Failed to fetch states:", error);
        setStateOptions([]);
      }
    } finally {
      if (fetchId === stateFetchIdRef.current) {
        setLoadingStates(false);
      }
    }
  }, []);

  // Fetch cities by country ID with optional search term and state filter
  const fetchCitiesByCountry = useCallback(async (countryId, searchTerm = '', stateFilter = '') => {
    if (!countryId) {
      setCityOptions([]);
      return;
    }

    const fetchId = ++cityFetchIdRef.current;
    setLoadingCities(true);

    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (stateFilter) params.state = stateFilter;

      const response = await axiosInstance.get(`/api/selections/countries/${countryId}/cities`, { params });

      if (fetchId === cityFetchIdRef.current && response.data.success) {
        setCityOptions(response.data.data || []);
      }
    } catch (error) {
      if (fetchId === cityFetchIdRef.current) {
        console.error("Failed to fetch cities:", error);
        setCityOptions([]);
      }
    } finally {
      if (fetchId === cityFetchIdRef.current) {
        setLoadingCities(false);
      }
    }
  }, []);

  // Debounced state search
  const triggerStateSearch = useCallback((searchTerm) => {
    if (stateSearchTimeoutRef.current) {
      clearTimeout(stateSearchTimeoutRef.current);
    }

    const countryId = selectedCountryIdRef.current;
    if (!countryId) return;

    if (searchTerm.length > 0) {
      stateFetchIdRef.current++;
      setStateOptions([]);
      setLoadingStates(true);
    }

    stateSearchTimeoutRef.current = setTimeout(() => {
      fetchStatesByCountry(countryId, searchTerm);
    }, 150);
  }, [fetchStatesByCountry]);

  // Debounced city search
  const triggerCitySearch = useCallback((searchTerm) => {
    if (citySearchTimeoutRef.current) {
      clearTimeout(citySearchTimeoutRef.current);
    }

    const countryId = selectedCountryIdRef.current;
    const stateFilter = selectedStateRef.current;
    if (!countryId) return;

    if (searchTerm.length > 0) {
      cityFetchIdRef.current++;
      setCityOptions([]);
      setLoadingCities(true);
    }

    citySearchTimeoutRef.current = setTimeout(() => {
      fetchCitiesByCountry(countryId, searchTerm, stateFilter);
    }, 150);
  }, [fetchCitiesByCountry]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (citySearchTimeoutRef.current) {
        clearTimeout(citySearchTimeoutRef.current);
      }
      if (stateSearchTimeoutRef.current) {
        clearTimeout(stateSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleCountryChange = (selectedOption) => {
    setUserCountry(selectedOption ? selectedOption.value : "");
    setUserState(""); // Clear state when country changes
    setUserCity(""); // Clear city when country changes

    // Clear any pending searches
    if (citySearchTimeoutRef.current) {
      clearTimeout(citySearchTimeoutRef.current);
    }
    if (stateSearchTimeoutRef.current) {
      clearTimeout(stateSearchTimeoutRef.current);
    }

    // Store country ID in ref for search functionality
    selectedCountryIdRef.current = selectedOption?.id || null;
    selectedStateRef.current = null;

    if (selectedOption?.id) {
      fetchStatesByCountry(selectedOption.id, '');
      setCityOptions([]); // Clear cities until state is selected
    } else {
      setStateOptions([]);
      setCityOptions([]);
    }
  };

  const handleStateChange = (selectedOption) => {
    setUserState(selectedOption ? selectedOption.value : "");
    setUserCity(""); // Clear city when state changes

    // Clear any pending city search
    if (citySearchTimeoutRef.current) {
      clearTimeout(citySearchTimeoutRef.current);
    }

    // Store state in ref for city search
    selectedStateRef.current = selectedOption?.value || null;

    const countryId = selectedCountryIdRef.current;
    if (countryId && selectedOption?.value) {
      fetchCitiesByCountry(countryId, '', selectedOption.value);
    } else if (countryId) {
      // If state is cleared, fetch all cities for country
      fetchCitiesByCountry(countryId, '', '');
    } else {
      setCityOptions([]);
    }
  };

  const handleCityChange = (selectedOption) => {
    setUserCity(selectedOption ? selectedOption.label : "");
  };

  const handleTimezoneChange = (selectedOption) => {
    setUserTimezone(selectedOption ? selectedOption.value : "UTC");
  };

  const getDefaultCountryOption = () =>
    countryOptions.find((option) => option.value === UserCountry);

  const getDefaultStateOption = () =>
    stateOptions.find((option) => option.value === UserState);

  const getDefaultCityOption = () =>
    cityOptions.find((option) => option.label === UserCity);

  const getDefaultTimezoneOption = () =>
    timezoneOptions.find((option) => option.value === UserTimezone);

  // Fetch states when country options are loaded and user has a country
  useEffect(() => {
    if (UserCountry && countryOptions.length > 0) {
      const countryObj = countryOptions.find((c) => c.value === UserCountry);
      if (countryObj?.id) {
        selectedCountryIdRef.current = countryObj.id;
        fetchStatesByCountry(countryObj.id, '');
      }
    }
  }, [UserCountry, countryOptions, fetchStatesByCountry]);

  // Fetch cities when state options are loaded and user has a state
  useEffect(() => {
    if (UserState && stateOptions.length > 0 && selectedCountryIdRef.current) {
      selectedStateRef.current = UserState;
      fetchCitiesByCountry(selectedCountryIdRef.current, '', UserState);
    }
  }, [UserState, stateOptions, fetchCitiesByCountry]);

  const handleSaveButtonClick = async () => {
    const commonData = {
      fname: UserfName,
      lname: UserlName,
      address_one: UserAddress,
      country: UserCountry,
      city: UserCity,
      state: UserState,
      zip: UserZip,
      timezone: UserTimezone,
    };

    if (selectedFile) {
      const validExtensions = ["image/jpeg", "image/jpg", "image/png"];
      if (!validExtensions.includes(selectedFile.type)) {
        setMessage("Invalid file type. Only JPG, JPEG, and PNG are allowed.");
        return;
      }

      const formData = new FormData();
      formData.append("avatar", selectedFile);
      Object.keys(commonData).forEach((key) =>
        formData.append(key, commonData[key])
      );

      try {
        const response = await axiosInstance.post(`/update-user-profile`, formData);
        setMessage(response.data.message);
      } catch (error) {
        setMessage("Error updating user profile with image.");
        console.error(error);
      }
    } else {
      const data = { ...commonData, profile_image: UserProfile };

      axiosInstance
        .post(`/update-user-profile`, data)
        .then((response) => setMessage(response.data.message))
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "An unknown error occurred.";
          setMessage(errorMessage);
        });
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteMessage("");
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const handleDeleteAccount = async () => {
    handleCloseDeleteDialog();
    setDeleteMessage("Deleting your account, please wait...");
    try {
      await axiosInstance.delete(`/delete-user-account`);
      setDeleteMessage(
        "Your account has been successfully deleted. You will be logged out now."
      );
      setTimeout(() => {
        localStorage.clear();
        navigate("/log-out");
      }, 3000);
    } catch (error) {
      console.error("Error deleting account:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Error: Could not delete your account. Please try again.";
      setDeleteMessage(errorMessage);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  return (
    <>

      <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 700 }} color="textPrimary">
            User Profile
          </Typography>
          <Divider sx={{ mb: 3 }} />
        <Box sx={{ flexGrow: 1, bgcolor: "background.paper", p: 3 }}>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: "100%" }}>
              <CardHeader title="Profile Settings" sx={{ textAlign: "center" }} />
              <Divider />
              <Tabs
                orientation="vertical"
                value={value}
                onChange={handleChange}
                aria-label="User Profile Tabs"
                sx={{ borderRight: 1, borderColor: "divider" }}
              >
                <Tab label="Account" {...a11yProps(0)} />
                <Tab label="Change Password" {...a11yProps(1)} />
                <Tab label="Delete Account" {...a11yProps(2)} />
              </Tabs>
            </Card>
          </Grid>

          <Grid item xs={12} md={9}>
            <Card>
              <TabPanel value={value} index={0}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Account Settings
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Avatar Section */}
                    <Grid item xs={12} md={3}>
                      <Box textAlign="center">
                        <Avatar
                          src={
                            UserProfile
                              ? `/${UserProfile}`
                              : "img/avatars/avatar.jpg"
                          }
                          sx={{
                            width: 128,
                            height: 128,
                            margin: "auto",
                            mb: 2,
                          }}
                        />
                        <Button variant="outlined" component="label">
                          Upload Image
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>
                        <Typography variant="caption" display="block" mt={1}>
                          Use an image at least 128x128px (.jpg, .jpeg, .png)
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Form Fields Section */}
                    <Grid item xs={12} md={9}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <TextField
                            label="First Name"
                            fullWidth
                            value={UserfName}
                            onChange={(e) => setUserfName(e.target.value)}
                          />
                          <TextField
                            label="Last Name"
                            fullWidth
                            value={UserlName}
                            onChange={(e) => setUserlName(e.target.value)}
                          />
                        </Box>

                        <TextField
                          label="Email"
                          type="email"
                          fullWidth
                          value={UserEmail}
                          disabled
                        />

                        <TextField
                          label="Address"
                          fullWidth
                          value={UserAddress}
                          onChange={(e) => setUserAddress(e.target.value)}
                        />

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" mb={1}>
                              Country
                            </Typography>
                            <Select
                              options={countryOptions}
                              value={getDefaultCountryOption()}
                              onChange={handleCountryChange}
                              isClearable
                              placeholder="Select country..."
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" mb={1}>
                              State/Province
                            </Typography>
                            <Select
                              options={stateOptions}
                              value={getDefaultStateOption()}
                              onChange={handleStateChange}
                              onInputChange={(inputValue, { action }) => {
                                if (action === 'input-change') {
                                  triggerStateSearch(inputValue);
                                }
                              }}
                              isClearable
                              isLoading={loadingStates}
                              isDisabled={!UserCountry}
                              placeholder={!UserCountry ? "Select a country first" : "Type to search states..."}
                              noOptionsMessage={() => !UserCountry ? "Select a country first" : loadingStates ? "Loading..." : "No states found"}
                              filterOption={() => true}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" mb={1}>
                              City
                            </Typography>
                            <Select
                              options={cityOptions}
                              value={getDefaultCityOption()}
                              onChange={handleCityChange}
                              onInputChange={(inputValue, { action }) => {
                                if (action === 'input-change') {
                                  triggerCitySearch(inputValue);
                                }
                              }}
                              isClearable
                              isLoading={loadingCities}
                              isDisabled={!UserCountry}
                              placeholder={!UserCountry ? "Select a country first" : "Type to search cities..."}
                              noOptionsMessage={() => !UserCountry ? "Select a country first" : loadingCities ? "Loading..." : "No cities found"}
                              filterOption={() => true}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <TextField
                              label="Zip Code"
                              fullWidth
                              value={UserZip}
                              onChange={(e) => setUserZip(e.target.value)}
                            />
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="body2" mb={1}>
                            Timezone
                          </Typography>
                          <Select
                            options={timezoneOptions}
                            value={getDefaultTimezoneOption()}
                            onChange={handleTimezoneChange}
                            isClearable={false}
                            placeholder="Select timezone..."
                          />
                          <Typography variant="caption" color="text.secondary">
                            Helps the AI agent schedule meetings correctly.
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSaveButtonClick}
                        >
                          Save Changes
                        </Button>

                        {message && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            {message}
                          </Alert>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </TabPanel>

              <TabPanel value={value} index={1}>
                <ResetPassword />
              </TabPanel>

              <TabPanel value={value} index={2}>
                <CardContent>
                  <Typography variant="h6" color="error" gutterBottom>
                    Delete Account
                  </Typography>
                  <Typography paragraph>
                    Once you delete your account, there is no going back. All of
                    your data, including website content, documents, images,
                    audio, and chatbot data will be permanently erased.
                  </Typography>
                  <Typography color="error" fontWeight="bold">
                    This action is irreversible. Please be certain.
                  </Typography>

                  <Button
                    variant="contained"
                    color="error"
                    sx={{ mt: 2 }}
                    onClick={handleOpenDeleteDialog}
                  >
                    Delete My Account
                  </Button>

                  {deleteMessage && (
                    <Alert
                      severity={
                        deleteMessage.includes("Error") ? "error" : "success"
                      }
                      sx={{ mt: 2 }}
                    >
                      {deleteMessage}
                    </Alert>
                  )}
                </CardContent>
              </TabPanel>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-account-dialog-title"
        aria-describedby="delete-account-dialog-description"
      >
        <DialogTitle id="delete-account-dialog-title">
          Are you absolutely sure?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-account-dialog-description">
            This action cannot be undone. This will permanently delete your
            account and remove all your data from our servers. This includes all
            website content, documents, images, audio, and chatbot data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" autoFocus>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Paper>
    </>
  );
}
